import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { useUpdateCase } from "src/cases/queries/cases";
import {
  CASE_QUEUES_QUERY_KEY_PREFIX,
  caseQueuesKeys,
  useCaseQueue,
} from "src/cases/queries/queues";
import { useCaseTemplateVersion } from "src/cases/queries/templates";
import { useQueueMembers } from "src/cases/queues/hooks";
import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { CasesPageParams } from "src/router/urls";
import { useCurrentUserId, useIsFWDOrAdmin } from "src/store/AuthStore";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import { useParamsDecode } from "src/utils/useParamsDecode";

type AssignCaseParams = {
  caseId: string;
  etag: string;
  queueId?: string;
  assigneeId?: string | null;
};

export const useAssignCase = (queueId: string) => {
  const {
    mutateAsync: updateCase,
    variables,
    isPending,
  } = useUpdateCase(queueId);

  const onAssign = async (params: AssignCaseParams) => {
    try {
      await updateCase({
        id: params.caseId,
        assigneeId: params.assigneeId,
        ifMatch: params.etag,
      });
    } catch (error) {
      logger.error("Error assigning case", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Assigning failed",
          description:
            "The case has been updated since you last loaded it. Please refresh the page and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Assigning failed",
          description:
            error.response?.data.errors[0]?.detail ?? "Unknown error",
        });
        return;
      }

      toastActions.failure({
        title: "Assigning failed",
        description:
          "Unexpected error occurred while assigning the case. Please try again later.",
      });
    }
  };

  return {
    onAssign,
    variables,
    isPending,
  };
};

export enum TemplateStatus {
  Outdated = "Outdated",
  Deleted = "Deleted",
  Active = "Active",
}

export const useTemplateStatus = (params: {
  queueId?: string;
  templateId?: string;
  versionId?: string;
  schemaVersion?: number;
}): { isLoading: boolean; status?: TemplateStatus } => {
  const { queueId, templateId, versionId, schemaVersion } = params;
  const { data: queue, isLoading: isLoadingQueue } = useCaseQueue(queueId);
  const { data: version, isLoading: isLoadingTemplateVersion } =
    useCaseTemplateVersion({
      templateId: templateId,
      versionId: versionId,
    });

  const isLoading = isLoadingQueue || isLoadingTemplateVersion;

  const status = ((): TemplateStatus | undefined => {
    if (!queue) return;

    const isActiveTemplate = queue?.template_configs?.some(
      (config) => config.template.id === templateId,
    );

    if (!isActiveTemplate) return TemplateStatus.Deleted;

    const isOutdatedTemplateVersion =
      version?.attributes?.schema_version !== undefined &&
      schemaVersion !== undefined &&
      version.attributes.schema_version > schemaVersion;

    if (isOutdatedTemplateVersion) return TemplateStatus.Outdated;

    return TemplateStatus.Active;
  })();

  return {
    isLoading,
    status,
  };
};

export const useRefreshQueue = () => {
  const queryClient = useQueryClient();
  const { queue_id } = useParamsDecode<CasesPageParams>();
  const { workspace } = useWorkspaceContext();

  const isFetching = useIsFetching({
    predicate: ({ queryKey }) =>
      queryKey.includes(CASE_QUEUES_QUERY_KEY_PREFIX) &&
      queryKey.includes(workspace.id) &&
      queryKey.includes(queue_id),
    exact: false,
  });

  const refreshQueue = async () => {
    if (isFetching) return;

    await queryClient.invalidateQueries({
      queryKey: caseQueuesKeys.get(workspace.id, queue_id),
    });
  };

  return { refreshQueue, isRefreshingQueue: !!isFetching };
};

/**
 * Whether the current user has joined a queue. Only members are assignable, so
 * this also gates claiming, which self-assigns.
 */
export const useIsQueueMember = (queueId?: string) => {
  const currentUserId = useCurrentUserId();
  const { queueMemberIds } = useQueueMembers(queueId);

  return !!currentUserId && queueMemberIds.has(currentUserId);
};

/**
 * Whether the current user may work on the cases of a queue: they are a member
 * of it, or they are a workspace admin or FWD user, who reach every queue. This
 * is not a check for assignability, which is restricted to queue members.
 */
export const useCanWorkOnQueue = (queueId?: string) => {
  const { workspaceRole } = useCapabilities();
  const isFWDOrAdmin = useIsFWDOrAdmin();
  const isQueueMember = useIsQueueMember(queueId);

  return (
    workspaceRole === WorkspaceSimpleRoleType.ADMIN ||
    isFWDOrAdmin ||
    isQueueMember
  );
};
